<template>
	<div class="map-tiff-test-page">
		<div class="map-toolbar">
			<div class="toolbar-title">GeoTIFF 地图测试</div>
			<div class="toolbar-status" :class="{ 'is-error': errorMessage }">
				{{ statusText }}
			</div>
		<div class="toolbar-meta">
			原图 {{ imageInfo.originalWidth }} x {{ imageInfo.originalHeight }}
			渲染 {{ imageInfo.previewWidth }} x {{ imageInfo.previewHeight }}
		</div>
		<div class="toolbar-note">
			当前显示为浏览器直接分块读取的 GeoTIFF 底图
		</div>
		</div>
		<div ref="mapContainer" class="map-container"></div>
	</div>
</template>

<script>
	import 'ol/ol.css'
	import Map from 'ol/Map'
	import View from 'ol/View'
	import TileLayer from 'ol/layer/Tile'
	import XYZ from 'ol/source/XYZ'
	import { fromLonLat } from 'ol/proj'
	import { defaults as defaultControls } from 'ol/control'
	import GeoImageSource from 'ol-ext/source/GeoImage'
	import GeoImageLayer from 'ol-ext/layer/GeoImage'

	const OVERLAY_IMAGE_PATH = 'map-overlay/GF2_WebReady_full.png'

	function getOverlayImageUrl() {
		const baseUrl = process.env.BASE_URL || '/'
		return `${baseUrl.replace(/\/$/, '')}/${OVERLAY_IMAGE_PATH}`
	}

	const GEO_REFERENCED_POINTS = {
		center: [100.418357, 38.970493],
		topLeft: [100.318134, 39.091844],
		topRight: [100.577969, 39.043688],
		bottomRight: [100.51824, 38.848965],
		bottomLeft: [100.259105, 38.897047],
	}

	const IMAGE_INFO = {
		originalWidth: 7242,
		originalHeight: 6882,
		previewWidth: 7242,
		previewHeight: 6882,
	}

	function getProjectedPoints() {
		return {
			center: fromLonLat(GEO_REFERENCED_POINTS.center),
			topLeft: fromLonLat(GEO_REFERENCED_POINTS.topLeft),
			topRight: fromLonLat(GEO_REFERENCED_POINTS.topRight),
			bottomRight: fromLonLat(GEO_REFERENCED_POINTS.bottomRight),
			bottomLeft: fromLonLat(GEO_REFERENCED_POINTS.bottomLeft),
		}
	}

	function getOverlayMetrics() {
		const points = getProjectedPoints()
		const topDx = points.topRight[0] - points.topLeft[0]
		const topDy = points.topRight[1] - points.topLeft[1]
		const bottomDx = points.bottomRight[0] - points.bottomLeft[0]
		const bottomDy = points.bottomRight[1] - points.bottomLeft[1]
		const leftDx = points.topLeft[0] - points.bottomLeft[0]
		const leftDy = points.topLeft[1] - points.bottomLeft[1]
		const rightDx = points.topRight[0] - points.bottomRight[0]
		const rightDy = points.topRight[1] - points.bottomRight[1]

		const averageWidth =
			(Math.hypot(topDx, topDy) + Math.hypot(bottomDx, bottomDy)) / 2
		const averageHeight =
			(Math.hypot(leftDx, leftDy) + Math.hypot(rightDx, rightDy)) / 2

		return {
			points,
			rotation: Math.atan2(topDy, topDx),
			scale: [averageWidth / IMAGE_INFO.originalWidth, averageHeight / IMAGE_INFO.originalHeight],
			mask: [points.topLeft, points.topRight, points.bottomRight, points.bottomLeft],
		}
	}

	export default {
		name: 'MapTiffTest',
		data() {
			return {
				map: null,
				imageInfo: IMAGE_INFO,
				loading: true,
				errorMessage: '',
			}
		},
		computed: {
			statusText() {
				if (this.errorMessage) {
					return this.errorMessage
				}

				if (this.loading) {
					return '正在加载地图影像覆盖层...'
				}

				return '地图影像覆盖层已加载'
			},
		},
		mounted() {
			this.initMap()
		},
		beforeDestroy() {
			if (this.map) {
				this.map.setTarget(null)
				this.map = null
			}
		},
		methods: {
			initMap() {
				try {
					const overlayImageUrl = getOverlayImageUrl()
					const overlayMetrics = getOverlayMetrics()
					const overlayImage = new Image()
					overlayImage.crossOrigin = 'anonymous'
					overlayImage.onload = () => {
						this.loading = false
						this.errorMessage = ''
					}
					overlayImage.onerror = () => {
						this.loading = false
						this.errorMessage = `地图影像覆盖层加载失败: ${overlayImageUrl}`
					}
					overlayImage.src = overlayImageUrl

					const overlaySource = new GeoImageSource({
						image: overlayImage,
						projection: 'EPSG:3857',
						imageCenter: overlayMetrics.points.center,
						imageScale: overlayMetrics.scale,
						imageRotate: overlayMetrics.rotation,
						imageMask: overlayMetrics.mask,
					})

					this.map = new Map({
						target: this.$refs.mapContainer,
						layers: [
							new TileLayer({
								source: new XYZ({
									url: 'https://map.snkoudai.com/maps/vt/lyrs=s&hl=zh-CN&gl=cn&scale=1&x={x}&y={y}&z={z}',
									maxZoom: 18,
								}),
							}),
							new GeoImageLayer({
								source: overlaySource,
								opacity: 0.82,
							}),
						],
						view: new View({
							projection: 'EPSG:3857',
							center: overlayMetrics.points.center,
							zoom: 13,
							minZoom: 8,
							maxZoom: 20,
						}),
						controls: defaultControls({
							attribution: false,
							rotate: false,
						}),
					})

					this.map.getView().fit(overlaySource.getExtent(), {
						padding: [72, 72, 72, 72],
						duration: 0,
						nearest: true,
					})
				} catch (error) {
					console.error(error)
					this.errorMessage = '地图影像覆盖初始化失败，请检查控制台日志'
					this.loading = false
				}
			},
		},
	}
</script>

<style scoped>
	.map-tiff-test-page {
		position: relative;
		width: 100%;
		height: 100vh;
		background: #041423;
	}

	.map-toolbar {
		position: absolute;
		top: 16px;
		left: 16px;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 16px;
		background: rgba(4, 20, 35, 0.78);
		border: 1px solid rgba(71, 178, 255, 0.28);
		border-radius: 8px;
		color: #d8ecff;
		backdrop-filter: blur(10px);
	}

	.toolbar-title {
		font-size: 16px;
		font-weight: 600;
		white-space: nowrap;
	}

	.toolbar-status,
	.toolbar-meta,
	.toolbar-note {
		font-size: 13px;
		line-height: 1.4;
	}

	.toolbar-status {
		color: #72d39b;
	}

	.toolbar-status.is-error {
		color: #ff8b8b;
	}

	.toolbar-note {
		color: rgba(216, 236, 255, 0.78);
	}

	.map-container {
		width: 100%;
		height: 100%;
	}
</style>
